import React, { useState } from 'react';
import {
    ConfigProvider,
    theme,
    Input,
    Select,
    DatePicker,
    Button,
    Upload,
    Avatar,
} from 'antd';
import {
    ChevronDown,
    ChevronRight,
    Search,
    Calendar,
    X,
    Plus,
    UploadCloud
} from 'lucide-react';
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

import Layout from '../../Layout'; // Giữ nguyên layout của bạn
import { Link } from "react-router-dom";

const { TextArea } = Input;
const { Dragger } = Upload;

export default function AddContractForm() {
    const [isDarkMode, setIsDarkMode] = useState(false);

    // 1. Quản lý trạng thái Đóng/Mở (Collapse)
    const [expandedSections, setExpandedSections] = useState({
        general: true,
        products: true,
        payment: true,
        description: true,
        attachment: true,
        custom: true
    });

    const toggleSection = (key) => {
        setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // 2. Data và State cho Hàng hóa (Dynamic Fields)
    const [products, setProducts] = useState([
        { id: 1, product: 'VPP005 - Bìa hồ sơ', unit: 'Cái', price: '1,000,000', qty: '101', discountType: '%', discountAmount: 'Số tiền', total: '101,000,000' },
        { id: 2, product: 'SP.VPP.122024.1 - Giấy A4 Paper One', unit: 'Thùng', price: '0', qty: '1', discountType: '%', discountAmount: 'Số tiền', total: '0' },
    ]);

    const handleAddProduct = () => {
        setProducts([...products, { id: Date.now(), product: null, unit: '', price: '', qty: '', discountType: '%', discountAmount: '', total: '' }]);
    };
    const handleRemoveProduct = (id) => {
        setProducts(products.filter(p => p.id !== id));
    };

    // 3. Data và State cho Thanh toán (Dynamic Fields)
    const [payments, setPayments] = useState([
        { id: 1, time: 'Lần 1', date: null, percent: '', amount: '', note: '' }
    ]);

    const handleAddPayment = () => {
        setPayments([...payments, { id: Date.now(), time: `Lần ${payments.length + 1}`, date: null, percent: '', amount: '', note: '' }]);
    };
    const handleRemovePayment = (id) => {
        setPayments(payments.filter(p => p.id !== id));
    };

    // Mock Data cho Select Nhà cung cấp
    const supplierOptions = [
        { value: 'kh1', label: 'KH0001 - Trần Thị Huệ' },
        { value: 'kh2', label: 'KH0002 - Phạm Tuyết Nhung' },
        { value: 'kh3', label: 'KH0003 - Nguyễn Hữu Anh' },
        { value: 'kh4', label: 'KH0004 - Ngô Thị Vân' },
        { value: 'kh5', label: 'KH0005 - Đào Công Danh' },
        { value: 'KHOTHI', label: 'KHOTHI.1224.01 - Công Ty TNHH TMDV Dầu Khí Hoàng Thủy' },
    ];

    // Mock Data cho Select Hàng hóa
    const productOptions = [
        { value: 'sp1', label: 'VPP001 - Bảng văn phòng', stock: '2489 (Cái)', img: 'https://i.pravatar.cc/150?img=1' },
        { value: 'sp2', label: 'DGD001 - Máy giặt', stock: '0', img: null },
        { value: 'sp3', label: 'VA102 - Bếp điện từ độ lập âm Bluestone ICB-6818', stock: '6 (Cái)', img: 'https://i.pravatar.cc/150?img=2' },
        { value: 'sp4', label: 'DA011 - Gạch', stock: '0', img: null },
        { value: 'sp5', label: 'SP.XD.122024.1 - Xi măng Hà Tiên PCB40', stock: '11196 (Bao)', img: 'https://i.pravatar.cc/150?img=3' },
    ];

    // Theme Config (Đổi mọi thứ sang màu #3e91ab)
    const antdThemeConfig = {
        token: {
            colorPrimary: '#3e91ab',
            colorBgContainer: isDarkMode ? '#1f2937' : '#ffffff',
            colorText: isDarkMode ? '#f3f4f6' : '#374151',
            colorBorder: isDarkMode ? '#4b5563' : '#d1d5db',
            borderRadius: 6,
            controlHeight: 40,
        },
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
    };

    const SectionHeader = ({ title, sectionKey }) => (
        <div
            onClick={() => toggleSection(sectionKey)}
            className="flex items-center gap-2 text-[#3e91ab] font-medium text-sm mb-4 mt-8 first:mt-0 cursor-pointer hover:opacity-80 transition-opacity select-none"
        >
            {expandedSections[sectionKey] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            <span>{title}</span>
        </div>
    );

    return (
        <Layout>
            <ConfigProvider theme={antdThemeConfig}>
                <div className={`min-h-screen pb-10 transition-colors duration-300`}>

                    {/* Header cố định */}
                    <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 md:px-6 py-4 sticky top-[64px] z-10 shadow-sm">
                        <div className="flex items-center gap-2 text-sm mb-2">
                            <Link to="/sales-contract" className="flex items-center gap-2">
                                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#3e91ab] hover:bg-[#357f96] text-white">
                                    <LeftOutlined className="text-[10px]" />
                                </div>
                            </Link>
                            <Link to="/sales-contract" className="text-gray-600 dark:text-gray-400 hover:text-[#3e91ab]">
                                Hợp đồng
                            </Link>
                            <span><RightOutlined className="w-3 h-3 text-gray-400" /></span>
                            <span className="text-gray-800 dark:text-gray-200 font-medium">Thêm mới hợp đồng</span>
                        </div>
                        <h2 className="text-lg text-gray-900 font-semibold t-text-white">
                            Thêm mới HĐ
                        </h2>
                    </div>

                    {/* Nội dung form */}
                    <div className="w-full mx-auto rounded-lg shadow-sm p-4 md:p-6 mt-6 dark:bg-gray-800 bg-white border dark:border-gray-700">

                        {/* --- 1. THÔNG TIN CHUNG --- */}
                        <SectionHeader title="Thông tin chung" sectionKey="general" />
                        {expandedSections.general && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in slide-in-from-top-2 mb-6">
                                <Input placeholder="Mã hợp đồng *" />
                                <Select placeholder="Tên hợp đồng *" className="w-full" options={[{ value: '1', label: 'Hợp đồng mua bán' }]} />

                                <Select
                                    showSearch
                                    placeholder="Nhà cung cấp"
                                    className="md:col-span-2 w-full"
                                    suffixIcon={<Search size={16} className="text-gray-400" />}
                                    options={supplierOptions}
                                />

                                <Select
                                    showSearch
                                    placeholder="Người theo dõi"
                                    className="md:col-span-2 w-full"
                                    suffixIcon={<Search size={16} className="text-gray-400" />}
                                    options={[]}
                                />

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:col-span-2">
                                    <DatePicker placeholder="Ngày ký *" className="w-full" suffixIcon={<Calendar size={16} className="text-gray-400" />} format="DD/MM/YYYY" />
                                    <DatePicker placeholder="Hiệu lực từ ngày *" className="w-full" suffixIcon={<Calendar size={16} className="text-gray-400" />} format="DD/MM/YYYY" />
                                    <DatePicker placeholder="Hiệu lực đến ngày" className="w-full" suffixIcon={<Calendar size={16} className="text-gray-400" />} format="DD/MM/YYYY" />
                                </div>
                            </div>
                        )}

                        {/* --- 2. HÀNG HÓA --- */}
                        <div className="flex justify-between items-center mt-8 mb-4">
                            <div
                                onClick={() => toggleSection('products')}
                                className="flex items-center gap-2 text-[#3e91ab] font-medium text-sm cursor-pointer hover:opacity-80 transition-opacity select-none"
                            >
                                {expandedSections.products ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                <span>Hàng hóa</span>
                            </div>
                            {/* Nút VND */}
                            {expandedSections.products && (
                                <div className="flex items-center text-xs border dark:border-gray-600 rounded">
                                    <span className="px-3 py-1.5 border-r dark:border-gray-600 text-gray-600 dark:text-gray-300">VND</span>
                                    <span className="px-2 py-1.5 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"><X size={14} className="text-gray-400" /></span>
                                </div>
                            )}
                        </div>

                        {expandedSections.products && (
                            <div className="animate-in slide-in-from-top-2 mb-6">
                                <div className="overflow-x-auto pb-4">
                                    <div className="min-w-[1000px]">
                                        <div className="grid grid-cols-[40px_3fr_1fr_1.5fr_1fr_1fr_1.5fr_1.5fr_auto] gap-2 mb-2 text-xs font-medium text-gray-500 dark:text-gray-400 px-1 items-end">
                                            <span className="text-center">STT</span>
                                            <span>Sản phẩm, dịch vụ <span className="text-red-500">*</span></span>
                                            <span>Đơn vị</span>
                                            <span>Giá mua</span>
                                            <span>Số lượng <span className="text-red-500">*</span></span>
                                            <span>Ck (%)</span>
                                            <span>Tiền chiết khấu</span>
                                            <span>Giá trị hợp đồng <span className="text-red-500">*</span></span>
                                            <span className="w-6"></span>
                                        </div>

                                        {products.map((item, index) => (
                                            <div key={item.id} className="grid grid-cols-[40px_3fr_1fr_1.5fr_1fr_1fr_1.5fr_1.5fr_auto] gap-2 mb-3 items-center">
                                                <div className="text-center bg-gray-50 dark:bg-gray-700/50 border dark:border-gray-600 rounded h-10 flex items-center justify-center text-gray-500">{index + 1}</div>

                                                <Select
                                                    showSearch
                                                    defaultValue={item.product}
                                                    placeholder="Chọn sản phẩm..."
                                                    // 1. Thêm min-w-0 vào đây để chống phình to phá Grid
                                                    className="w-full min-w-0"
                                                    // 2. Không ép khung dropdown xổ xuống phải to theo text
                                                    popupMatchSelectWidth={false}
                                                    options={productOptions}
                                                    optionRender={(option) => (
                                                        <div className="flex items-center gap-3 py-1">
                                                            {option.data.img ? (
                                                                <Avatar src={option.data.img} />
                                                            ) : (
                                                                <Avatar className="bg-gray-200 text-gray-500 shrink-0">
                                                                    {option.data.label.charAt(0)}
                                                                </Avatar>
                                                            )}

                                                            {/* 3. Thêm min-w-0 vào div bọc chữ và truncate vào span để cắt chữ dài thành ... */}
                                                            <div className="flex flex-col min-w-0">
                                                                <span className="font-medium text-gray-800 dark:text-gray-200 truncate">
                                                                    {option.data.label}
                                                                </span>
                                                                <span className="text-[11px] text-gray-500 italic">
                                                                    Tồn kho: {option.data.stock}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}
                                                />
                                                <Input defaultValue={item.unit} />
                                                <Input defaultValue={item.price} className="text-right" />
                                                <Input defaultValue={item.qty} className="text-right" />
                                                <Select defaultValue={item.discountType} options={[{ value: '%', label: '%' }]} />
                                                <Input placeholder={item.discountAmount} className="text-right" />
                                                <Input defaultValue={item.total} className="text-right bg-gray-50 dark:bg-gray-700/50" readOnly />
                                                <X size={18} className="text-gray-400 cursor-pointer hover:text-red-500 ml-1" onClick={() => handleRemoveProduct(item.id)} />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <button onClick={handleAddProduct} className="flex items-center justify-center w-6 h-6 rounded-full border border-[#3e91ab] text-[#3e91ab] hover:bg-[#3e91ab] hover:text-white transition-colors mb-6">
                                    <Plus size={14} />
                                </button>

                                {/* Tổng cộng (Nằm bên phải) */}
                                <div className="flex justify-end mt-4">
                                    <div className="w-full md:w-[450px] flex flex-col gap-3 text-sm">
                                        <div className="flex justify-between items-center gap-4">
                                            <span className="text-gray-600 dark:text-gray-400 flex-1 text-right">Chiết khấu trên cả hợp đồng</span>
                                            <div className="flex gap-2 w-[220px]">
                                                <Input placeholder="%" className="w-[80px] text-right" />
                                                <Input placeholder="Hoặc số tiền" className="flex-1 text-right" />
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center gap-4">
                                            <span className="text-gray-600 dark:text-gray-400 flex-1 text-right">Thuế trên cả hợp đồng</span>
                                            <div className="flex gap-2 w-[220px]">
                                                <Input placeholder="%" className="w-[80px] text-right" />
                                                <Input placeholder="Hoặc số tiền" className="flex-1 text-right" />
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center gap-4">
                                            <span className="text-gray-600 dark:text-gray-400 flex-1 text-right">Phí trên tổng đơn hàng</span>
                                            <div className="flex items-center gap-2 w-[220px]">
                                                <Select defaultValue="Phi" className="w-[80px]" options={[{ value: 'Phi', label: 'Phí' }]} />
                                                <Input placeholder="Số tiền" className="flex-1 text-right" />
                                                <button className="text-[#3e91ab] border border-[#3e91ab] rounded-full p-0.5 shrink-0"><Plus size={12} /></button>
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center gap-4 mt-2">
                                            <span className="text-gray-800 dark:text-gray-200 font-medium flex-1 text-right">Tổng tiền sau thuế</span>
                                            <Input defaultValue="101,000,000" className="w-[220px] text-right bg-gray-50 dark:bg-gray-700/50 font-medium text-[#3e91ab]" readOnly />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* --- 3. THANH TOÁN --- */}
                        <SectionHeader title="Thanh toán" sectionKey="payment" />
                        {expandedSections.payment && (
                            <div className="animate-in slide-in-from-top-2 mb-6">
                                <div className="overflow-x-auto pb-4">
                                    <div className="min-w-[800px]">
                                        <div className="grid grid-cols-[80px_2fr_1fr_2fr_3fr_auto] gap-2 mb-2 text-xs font-medium text-gray-500 dark:text-gray-400 px-1 items-end">
                                            <span>Số</span>
                                            <span>Ngày thanh toán</span>
                                            <span>Thanh toán (%)</span>
                                            <span>Số tiền</span>
                                            <span>Nội dung thanh toán</span>
                                            <span className="w-6"></span>
                                        </div>

                                        {payments.map((item) => (
                                            <div key={item.id} className="grid grid-cols-[80px_2fr_1fr_2fr_3fr_auto] gap-2 mb-3 items-center">
                                                <Input value={item.time} readOnly className="bg-gray-50 dark:bg-gray-700/50 text-center" />
                                                <DatePicker className="w-full" format="DD/MM/YYYY" placeholder="dd/mm/yyyy" suffixIcon={<Calendar size={16} className="text-gray-400" />} />
                                                <Input placeholder="%" className="text-right" />
                                                <Input placeholder="Tiền" className="text-right" />
                                                <Input placeholder="Thanh toán..." />
                                                <X size={18} className="text-gray-400 cursor-pointer hover:text-red-500 ml-1" onClick={() => handleRemovePayment(item.id)} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <button onClick={handleAddPayment} className="flex items-center justify-center w-6 h-6 rounded-full border border-[#3e91ab] text-[#3e91ab] hover:bg-[#3e91ab] hover:text-white transition-colors">
                                    <Plus size={14} />
                                </button>
                            </div>
                        )}

                        {/* --- 4. MÔ TẢ --- */}
                        <SectionHeader title="Mô tả" sectionKey="description" />
                        {expandedSections.description && (
                            <div className="animate-in slide-in-from-top-2 mb-6">
                                <TextArea placeholder="Ghi chú hợp đồng" autoSize={{ minRows: 3, maxRows: 6 }} className="p-3" />
                            </div>
                        )}

                        {/* --- 5. ĐÍNH KÈM --- */}
                        <SectionHeader title="Đính kèm" sectionKey="attachment" />
                        {expandedSections.attachment && (
                            /* Đổi md:w-1/2 thành lg:w-1/2 để tablet hiển thị full chiều rộng */
                            <div className="animate-in slide-in-from-top-2 mb-6 w-full lg:w-1/2">
                                <Dragger className={`bg-transparent`} style={{ padding: '20px', border: `1px dashed ${isDarkMode ? '#4b5563' : '#d1d5db'}` }}>
                                    {/* Thêm flex-col cho mobile và sm:flex-row cho màn hình to hơn */}
                                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                        <div className="w-10 h-10 rounded-full bg-[#3e91ab]/10 flex items-center justify-center shrink-0">
                                            <UploadCloud size={20} className="text-[#3e91ab]" />
                                        </div>
                                        <div className="text-center sm:text-left">
                                            <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">
                                                Kéo thả file vào đây để tải lên hoặc
                                            </p>
                                            {/* Thêm flex-wrap để chống tràn nút */}
                                            <div className="flex flex-wrap justify-center sm:justify-start gap-2">
                                                <Button type="primary" size="small" style={{ backgroundColor: '#3e91ab' }}>
                                                    CHỌN TỪ MÁY
                                                </Button>
                                                <Button size="small" icon={<UploadCloud size={14} />}>
                                                    CHỌN TỪ CLOUD
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </Dragger>
                            </div>
                        )}

                        {/* --- 6. TRƯỜNG TÙY BIẾN --- */}
                        <SectionHeader title="Trường tùy biến" sectionKey="custom" />
                        {expandedSections.custom && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in slide-in-from-top-2 mb-6">
                                <Select defaultValue="1564" placeholder="Người ký" className="md:col-span-2 w-full" options={[{ value: '1564', label: '1564 - Nguyễn Thị Kim Thu' }]} />
                                <Select defaultValue="vin" placeholder="Brand" className="md:col-span-2 w-full" options={[{ value: 'vin', label: 'Công Ty Cổ Phần Kinh Doanh Và Quản Lý Bất Động Sản Vinhomes' }]} />
                                <Input placeholder="Job *" className="md:col-span-2" />
                            </div>
                        )}

                        {/* --- FOOTER BUTTONS --- */}
                        <div className="flex gap-3 pt-6 border-t dark:border-gray-700">
                            <Button type="primary" size="large" style={{ backgroundColor: '#3e91ab', fontWeight: 500 }}>
                                CẬP NHẬT
                            </Button>
                            <Button size="large" className="font-medium">
                                HỦY BỎ
                            </Button>
                        </div>
                    </div>
                </div>
            </ConfigProvider>
        </Layout>
    );
}